<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell :compact="true">
    <template #title>
      <div class="flex items-center gap-3">
        <!-- Toggle -->
        <div
          v-if="showToggle"
          class="session-toggle relative flex rounded-xl border-1 border-[var(--color-primary-500)] shadow-inner select-none cursor-pointer p-1 bg-[var(--color-primary-100)]"
          @click.stop="toggleValue = !toggleValue">
          <!-- Sliding background -->
          <div
            class="absolute inset-1 w-[calc(50%-0.25rem)] bg-green-700 rounded-lg shadow-md transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
            :style="{
              transform: toggleValue ? 'translateX(100%)' : 'translateX(0)',
            }"></div>

          <!-- Left Option -->
          <div
            class="relative z-10 grid place-items-center px-4 py-3 rounded-lg transition-colors duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
            :class="
              !toggleValue ? 'text-white' : 'text-[var(--color-primary-600)]'
            ">
            <Camera class="w-6 h-6" />
          </div>

          <!-- Right Option -->
          <div
            class="relative z-10 grid place-items-center px-4 py-3 rounded-lg transition-colors duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
            :class="
              toggleValue ? 'text-white' : 'text-[var(--color-primary-600)]'
            ">
            <Pencil class="w-6 h-6" />
          </div>
        </div>

        <div class="flex items-center gap-2">
          <component :is="icon" class="w-6 h-6" v-if="icon && !showToggle" />
          <span class="text-lg font-semibold">{{ title }}</span>
        </div>
      </div>
    </template>

    <template #action>
      <ArrowRight class="w-5 h-5" />
    </template>

    <div class="flex items-center justify-end gap-3">
      <!-- Default slot for content -->
      <slot />
    </div>
  </ModuleShell>
</template>

<script setup>
  import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
  import { ArrowRight, Camera, Pencil } from 'lucide-vue-next'
  import { ref, watch } from 'vue'

  const props = defineProps({
    title: { type: String, required: true },
    icon: { type: [Object, Function], default: null },
    showToggle: { type: Boolean, default: false },
    modelValue: { type: Boolean, default: false },
  })

  const emit = defineEmits(['update:modelValue'])
  const toggleValue = ref(props.modelValue)

  watch(toggleValue, (val) => emit('update:modelValue', val))
  watch(
    () => props.modelValue,
    (val) => (toggleValue.value = val)
  )
</script>
